/* eslint-disable @nx/enforce-module-boundaries */
'use-client';
import React, { useEffect } from 'react';
import {
  ActivityIcon,
  CalendarIcon,
  CircleIcon,
  CommentsIcon,
  FolderIcon,
  PageIcon,
  ReminderIcon,
  TagIcon,
  UsersIcon,
  TeamIcon,
  TriangleUp,
  TriangleDown,
  BackButton,
  HandShakeSimpleIcon,
  MoreActivityIcon,
  TeamNewIcon,
  AccordianDown,
  AccordianUp,
  CompanyIndustryIcon
} from '@mybridge/icons';
import { Text } from '@mybridge/ui/text';
import { useQueries } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import API from 'v4/store/common/api';
import { APIEndPoints } from 'v4/store/common/endPoint';
import AllToggle from './AllToggle';
import EmailToggle from './EmailToggle';
import PushToggle from './PushToggle';
import SMSToggle from './SMSToggle';
// import { getAllCompanyPages } from 'v4/store/actions/companyPages.Action';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Card,
  CardBody,
  Flex,
  Heading,
  Image,
  Spinner,
  HStack,
} from '@mybridge/ui';
import { trimStr } from 'v4/components/utils';
import TagSection from './TagSection';
import SectionLoader from 'v4/components/common/loader/sectionLoader';
import { AuthenticatedProviders } from 'v4/snippets/providers/authenticated';
import { getAllCompanyPages } from 'v4/store/actions/companyPages.actions';
import { isMobile } from 'react-device-detect';
import Head from 'next/head';
import { FolderDiskIcon } from '@mybridge/icons/NotificationIcons';
import { getFollowingPages } from 'v4/store/actions/connection.actions';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { ANALYTICS_EVENTS, trackEvent } from 'v4/utils/analyticsEvents';
export default function Notifications({ setSelectedComponent }) {
  const { t } = useTranslator();
  const [emailQuery, smsQuery, pushQuery, pages] = useQueries({
    queries: [
      {
        queryKey: ['email'],
        queryFn: async () => {
          return await API({
            method: 'GET',
            url: `${APIEndPoints.Notification}/email/`,
          });
        },
      },
      {
        queryKey: ['sms'],
        queryFn: async () => {
          return await API({
            method: 'GET',
            url: `${APIEndPoints.Notification}/sms/`,
          });
        },
      },
      {
        queryKey: ['push'],
        queryFn: async () => {
          return await API({
            method: 'GET',
            url: `${APIEndPoints.Notification}/push/`,
          });
        },
      },
    ],
  });

  const { userProfileInfo } = useSelector((state) => state.userProfile);
  const router = useRouter();
  const trackNotificationsPageVisit = () => {
      const userSlug = userProfileInfo?.slug;
      if (!userSlug) return;
    
      trackEvent(ANALYTICS_EVENTS.NotificationSettings, userSlug, {
        page_path: router.asPath,
        page_title: 'Notification Settings',
        timestamp: new Date().toISOString()
      });
  };
    
  useEffect(() => {
    trackNotificationsPageVisit();
  }, []);

  const dispatch = useDispatch();
  const { push } = useRouter();
  const { loggedIn, pageLoaded } = useSelector((state) => state.user);
  const { globalNotifCount } = useSelector((state) => state.notifications);
  const { companyPageList, loading } = useSelector(
    (state) => state.companyPage
  );
  const { myFollowingPages } = useSelector(
    (state) => state.connection
  );
  useEffect(() => {
    dispatch(getAllCompanyPages());
    dispatch(getFollowingPages({ query: '' }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (emailQuery.isLoading || smsQuery.isLoading || pushQuery.isLoading) {
    // return <Loader secondary={true} />;
    return <SectionLoader />;
  }

  if (emailQuery.isError || smsQuery.isError || pushQuery.isError) {
    return <div>Error...</div>;
  }

  const emailSettings = emailQuery?.data?.results || [];
  const smsSettings = smsQuery?.data?.results || [];
  const pushSettings = pushQuery?.data?.results || [];
  const defaultImage = "https://cdn.bybk.org/media/placeholder/logo_placeholder_image.png";

  const isSettingEnabled = (settings = [], moduleKey, companyPage = null) => {
    if (companyPage) {
      const companySetting = settings.find(
        (item) => item._for === moduleKey && item.company_page === companyPage
      );
      return companySetting ? Boolean(companySetting.enabled) : false;
    } else {
      const moduleSettings = settings.filter(
        (item) => item._for === moduleKey && !item.company_page
      );
      if (moduleSettings.length > 0) {
        const sortedSettings = [...moduleSettings].sort((a, b) => {
          const dateA = new Date(a.updated_at || a.created_at);
          const dateB = new Date(b.updated_at || b.created_at);
          return dateB - dateA;
        });
        return Boolean(sortedSettings[0].enabled);
      }
    }
    return false;
  };

  const getTranslated = (key, fallback) => {
    const val = t(key);
    return (val && val !== key) ? val : fallback;
  };

  const getCategorySummary = (key) => {
    if (key === 'tag') {
      const tagSetting = emailSettings.find((set) => set._for === 'tag');
      const val = tagSetting?.who_can_see;
      if (val === 'no_one') return getTranslated('settingNotification.noOne', 'No one');
      if (val === 'connections') return getTranslated('settingNotification.connections', 'Connections');
      return getTranslated('settingNotification.anyone', 'Anyone');
    }

    const isPush = isSettingEnabled(pushSettings, key);
    const isEmail = isSettingEnabled(emailSettings, key);
    const isSms = isSettingEnabled(smsSettings, key);

    const active = [];
    if (isPush) active.push(getTranslated('settingNotification.push', 'Push'));
    if (isEmail) active.push(getTranslated('settingNotification.email', 'Email'));
    if (isSms) active.push(getTranslated('settingNotification.sms', 'SMS'));

    return active.length > 0 ? active.join(', ') : getTranslated('settingNotification.off', 'Off');
  };

  const getCompanyPageSummary = (companyPageId) => {
    const isPush = isSettingEnabled(pushSettings, 'company_pages', companyPageId);
    const isEmail = isSettingEnabled(emailSettings, 'company_pages', companyPageId);
    const isSms = isSettingEnabled(smsSettings, 'company_pages', companyPageId);

    const active = [];
    if (isPush) active.push(getTranslated('settingNotification.push', 'Push'));
    if (isEmail) active.push(getTranslated('settingNotification.email', 'Email'));
    if (isSms) active.push(getTranslated('settingNotification.sms', 'SMS'));

    return active.length > 0 ? active.join(', ') : getTranslated('settingNotification.off', 'Off');
  };

  const sections = [
    {
      title: t('postActionMenu.handshakeRequest') || 'Handshake Request',
      // subTitle: t('settingNotification.pushEmailSms') || 'Push, Email, SMS',
      subTitle: getCategorySummary('connection'),
      icon: <HandShakeSimpleIcon width="24" height="24" color="#4170A4" />,
      description: t('settingNotification.handshakeRequest') || `Choose how you want to be notified when you receive a Handshake Request.`,
      key: 'connection',
    },
    {
      title: t('post.comments') || 'Comments',
      // subTitle: t('settingNotification.pushEmailSms') || 'Push, Email, SMS',
      subTitle: getCategorySummary('comments'),
      icon: <CommentsIcon width="24" height="24" color="#4170A4" />,
      description:
        t('settingNotification.postComment') || 'Choose how you want to be notified when your post receives a new comment.',
      key: 'comments',
    },
    {
      title: t('settingNotification.tag') || 'Tag',
      // subTitle: t('settingNotification.pushEmailSms') || 'Push, Email, SMS',
      subTitle: getCategorySummary('tag'),
      icon: <TagIcon width="24" height="24" color="#68A4D1" />,
      description: t('settingNotification.tagged') || `Choose how you want to be notified when you're are Tagged.`,
      key: 'tag',
      radio: true,
      component: (
        <TagSection
          whoCanSee={emailSettings.find((set) => set._for === 'tag')}
          onChange={emailQuery.refetch}
        />
      ),
    },
    {
      title: t('settingNotification.reminders') || 'Reminders',
      // subTitle: t('settingNotification.pushEmailSms') || 'Push, Email, SMS',
      subTitle: getCategorySummary('reminder'),
      icon: <ReminderIcon width="24" height="24" color="#4170A4" />,
      description: t('settingNotification.profilePost') || `Choose how you want to be notified when there are posts made on your profile or a connection agrees or disagrees to a post you've made.`,
      key: 'reminder',
      subDescription: t('settingNotification.whereToReceive') || `Where do you receive these notifications`,
      allowDenyAll: {},
    },
    {
      title: t('settingNotification.moreActivity') || 'More Activity About You',
      // subTitle: t('settingNotification.pushEmailSms') || 'Push, Email, SMS',
      subTitle: getCategorySummary('more_activity'),
      icon: <MoreActivityIcon width="24" height="24" color="#4170A4" />,
      description: t('settingNotification.profilePost') || `Choose how you want to be notified when there are posts made on your profile or a connection agrees or disagrees to a post you've made.`,
      key: 'more_activity',
      subDescription: t('settingNotification.whereToReceive') || `Where do you receive these notifications`,
      allowDenyAll: {},
    },
    {
      title: t('settingNotification.updatesFromCircle') || 'Updates from your circle',
      // subTitle: t('settingNotification.pushEmailSms') || 'Push, Email, SMS',
      subTitle: getCategorySummary('updates_from_friend'),
      icon: <TeamIcon width="24" height="24" color="#0D7FFE" />,
      description:
        t('settingNotification.connectionUpdate') || 'Choose how you want to be notified when your connections post or update their status.',
      key: 'updates_from_friend',
      subDescription: t('settingNotification.whereToReceive') || `Where do you receive these notifications`,
      allowDenyAll: {},
    },

    {
      title: t('settingNotification.teamNotification') || 'Team Notification',
      // subTitle: t('settingNotification.pushEmailSms') || 'Push, Email, SMS',
      subTitle: getCategorySummary('team'),
      icon: <TeamNewIcon width="24" height="24" color="#68A4D1" extraColor="#365E7D" />,
      description:
        t('settingNotification.connectionUpdate') || 'Choose how you want to be notified when your connections post or update their status.',
      key: 'team',
      subDescription: t('settingNotification.whereToReceive') || `Where do you receive these notifications`,
      allowDenyAll: {},
    },
    {
      title: t('settingNotification.diskNotification') || 'Disk Notification',
      // subTitle: t('settingNotification.pushEmailSms') || 'Push, Email, SMS',
      subTitle: getCategorySummary('disk'),
      icon: <FolderDiskIcon width="24" height="24" color="#0D7FFE" extraColor="#4170A4" />,
      description:
        t('settingNotification.connectionUpdate') || 'Choose how you want to be notified when your connections post or update their status.',
      key: 'disk',
      subDescription: t('settingNotification.whereToReceive') || `Where do you receive these notifications`,
      allowDenyAll: {},
    },

    {
      title: t('settingNotification.meetingsEvents') || 'Meetings and Events',
      // subTitle: t('settingNotification.pushEmailSms') || 'Push, Email, SMS',
      subTitle: getCategorySummary('event'),
      icon: <CalendarIcon width="24" height="24" color="#4170A4" extraColor="#68A4D1" />,
      description: t('settingNotification.meetingsAndEvents') || `Choose how you want to be notified about Meetings and Events.`,
      key: 'event',
      subDescription: t('settingNotification.whereToReceive') || `Where do you receive these notifications`,
      allowDenyAll: {},
    },
    ...(companyPageList?.length ? [{
      title: t('settingNotification.pagesProfilesManage') || 'Pages and profiles you manage',
      // subTitle: t('settingNotification.pushEmailSms') || 'Push, Email, SMS',
      subTitle: getCategorySummary('company_pages'),
      icon: <PageIcon width="24" height="24" color="#68A4D1" extraColor="#4170A4" />,
      description:
        t('settingNotification.pageActivity') || 'These are notifications about activity in Pages and profiles that you manage.',
      key: 'company_pages',
      component: (
        <>
          {companyPageList?.length ? (
            <Accordion allowToggle mt={3}>
              {companyPageList.map(({ id, name, image }, cpind) => (
                <AccordionItem key={cpind} eventKey={cpind}>
                  {({ isExpanded }) => (<><AccordionButton p={0}>
                    <Flex gap="4" py="15px" flex="1" px={0}>
                      {
                        <Link href={`/page/private/${id}`}>
                          {(image === defaultImage || image === null) ? 
                            <Flex
                              p={2}
                              bg="#D9D9D9"
                              width="44px"
                              height="44px"
                              justifyContent="center"
                              alignItems="center"
                              borderRadius="50%"
                            >
                              <CompanyIndustryIcon
                                width="25px"
                                height="25px"
                                color="#868585"
                              />
                            </Flex> :
                            <Box display='flex' alignItems='center' justifyContent='center' position='relative' w="44px" h="44px">
                              <Image
                                src={image}
                                alt={name}
                                height="44px"
                                width="44px"
                                borderRadius="50%"
                                background="white"
                                style={{
                                  position: "absolute",
                                  inset: 0
                                }}
                                loading="lazy"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none"; // hide image if broken
                                }}
                              />
                              <CompanyIndustryIcon
                                width="25px"
                                height="25px"
                                color="#868585"
                              />
                            </Box>
                          }
                        </Link> 
                      }
                      <Box>
                        <Text fontWeight="400" fontSize="16px" textAlign="left" color="brandGray.850">
                          {trimStr(name, { length: 50 })}
                        </Text>
                        <Text fontSize="14px" color="brandGray.850">
                          {/* {t('settingNotification.notificationsOnPageEmail') || "Notifications on page, email"} */}
                          {getCompanyPageSummary(id)}
                        </Text>
                      </Box>
                    </Flex>
                    {isExpanded ? (
                      <TriangleUp fontSize="12px" color="#5B5B5B" />
                    ) : (
                      <TriangleDown fontSize="12px" color="#5B5B5B" />
                    )}
                  </AccordionButton>
                    <AccordionPanel>
                      <Box ps="60px">
                        <PushToggle
                          onChange={pushQuery.refetch}
                          companyPage={id}
                          module={'company_pages'}
                          settings={pushSettings}
                        />
                        <EmailToggle
                          onChange={emailQuery.refetch}
                          companyPage={id}
                          module={'company_pages'}
                          settings={emailSettings}
                        />
                        <SMSToggle
                          onChange={smsQuery.refetch}
                          companyPage={id}
                          module={'company_pages'}
                          settings={smsSettings}
                        />
                      </Box>
                    </AccordionPanel></>)}

                </AccordionItem>
              ))}
            </Accordion>
          ) : loading ? (
            <Spinner />
          ) : (
            <div className="pt-2">
              {t('settingNotification.noCompanyPagesFound') || "No company pages found! The company pages that you manage will appear here."}
            </div>
          )}
        </>
      ),
    }] : []),
    ...(myFollowingPages?.count > 0 ? [{
      title: t('settingNotification.pagesProfilesFollow') || 'Pages and profiles you follow',
      // subTitle: t('settingNotification.pushEmailSms') || 'Push, Email, SMS',
      subTitle: getCategorySummary('following_pages_profiles'),
      icon: <PageIcon width="24" height="24" color="#68A4D1" extraColor="#4170A4" />,
      description:
        t('settingNotification.pageActivity') || 'These are notifications about activity in Pages and profiles that you follow.',
      // key: 'updates_from_friend',
      key: 'following_pages_profiles',
      allowDenyAll: {},
    }] : [])
  ];

  const refetchAll = async () => {
    try {
      // Use Promise.all to run all refetch operations in parallel
      await Promise.all([
        emailQuery?.refetch?.(),
        pushQuery?.refetch?.(),
        smsQuery?.refetch?.()
      ]);
    } catch (error) {
      console.error("Error refetching notification settings:", error);
    }
  };

  return (
    <AuthenticatedProviders>
      <Head>
        <title>
          {loggedIn && globalNotifCount ? `(${globalNotifCount})` : ''} {t('otherModules.settings') || "Settings"}
          - {t('common.notifications') || "Notifications"} | myBridge.me
        </title>
      </Head>
      <Accordion allowToggle p={[2, 0]}>
        {sections
          .filter((s) => s)
          ?.map(
            (
              {
                title,
                extra,
                description,
                subDescription,
                subTitle,
                icon,
                key,
                allowDenyAll,
                component,
              },
              i
            ) => (
              <AccordionItem border="0" key={i}>
                {({ isExpanded }) => {
                  // Calculate borderRadius based on isMobile and isExpanded
                  const borderRadius = isMobile ? (isExpanded ? "8px" : "30px") : "20px";

                  return (
                    <Card
                      //boxShadow="none"
                      boxShadow="md"
                      border="1px solid #D9D9D9"
                      mb={3}
                      borderRadius={borderRadius} // Use the calculated borderRadius
                    >

                      <CardBody p={[0, 3]}>
                        <AccordionButton _hover={{ background: 'transparent' }}>
                          <Box as="span" flex="1" textAlign="left">
                            <Flex>
                              <Box mt={1}>{icon}</Box>
                              <Box ms="15px">
                                <Text
                                  fontWeight="400"
                                  fontSize="16px"
                                  color="brandGray.850"
                                >
                                  {title}
                                </Text>
                                {!isMobile && (
                                  <Text fontSize="14px" color="brandGray.850">
                                    {subTitle}
                                  </Text>
                                )}
                              </Box>
                            </Flex>
                          </Box>
                          {/* <AccordionIcon /> */}
                          {isMobile ? (
                            isExpanded ? (
                              <AccordianUp fontSize="12px" />
                            ) : (
                              <AccordianDown fontSize="12px" />
                            )
                          ) : isExpanded ? (
                            <TriangleUp fontSize="12px" color="#5B5B5B" />
                          ) : (
                            <TriangleDown fontSize="12px" color="#5B5B5B" />
                          )}
                        </AccordionButton>
                        <AccordionPanel>
                          <Text fontSize="14px" color="brandGray.850">
                            {description}
                          </Text>
                          {allowDenyAll ? (
                            <div className="pb-3">
                              <AllToggle
                                onChange={refetchAll}
                                module={key}
                                settings={{
                                  pushSettings,
                                  emailSettings,
                                  smsSettings,
                                }}
                              />
                            </div>
                          ) : (
                            ''
                          )}
                          {subDescription ? (
                            <Text mt="15px" fontSize="14px" color="brandGray.850">
                              {subDescription}
                            </Text>
                          ) : (
                            ''
                          )}
                          {component ? (
                            component
                          ) : (
                            <>
                              <PushToggle
                                onChange={pushQuery.refetch}
                                module={key}
                                settings={pushSettings}
                              />
                              <EmailToggle
                                onChange={emailQuery.refetch}
                                module={key}
                                settings={emailSettings}
                              />
                              <SMSToggle
                                onChange={smsQuery.refetch}
                                module={key}
                                settings={smsSettings}
                              />
                            </>
                          )}
                        </AccordionPanel>
                      </CardBody>
                    </Card>
                  );
                }}
              </AccordionItem>
            )
          )}
      </Accordion>
    </AuthenticatedProviders>
  );
}
